/**
 * Offline Storage Service for Local Flyer Delivery
 * 
 * Handles storing delivery data locally when the user loses internet connection
 * and syncing it back to the server when the connection is restored.
 */

const OFFLINE_DATA_KEY = 'localflyer_offline_data';
const PENDING_SESSIONS_KEY = 'localflyer_pending_sessions';
const OFFLINE_GPS_POINTS_KEY = 'localflyer_offline_gps';

/**
 * Check if the browser is online
 */
export const isOnline = () => navigator.onLine;

/**
 * Cache a job's house locations so the street-marking distance check
 * still works when the app is opened offline.
 */
export const cacheJobHouses = (jobId, houses) => {
  try {
    if (!jobId || !Array.isArray(houses) || houses.length === 0) return false;
    localStorage.setItem(
      `localflyer_houses_cache_${jobId}`,
      JSON.stringify({ ts: new Date().toISOString(), houses })
    );
    return true;
  } catch (error) {
    console.error('Error caching job houses:', error);
    return false;
  }
};

export const getCachedJobHouses = (jobId) => {
  try {
    if (!jobId) return [];
    const raw = localStorage.getItem(`localflyer_houses_cache_${jobId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.houses) ? parsed.houses : [];
  } catch (error) {
    return [];
  }
};

/**
 * Save GPS point to local storage when offline
 */
export const saveOfflineGPSPoint = (point) => {
  try {
    const existingPoints = getOfflineGPSPoints();
    existingPoints.push({
      ...point,
      timestamp: new Date().toISOString(),
      synced: false
    });
    localStorage.setItem(OFFLINE_GPS_POINTS_KEY, JSON.stringify(existingPoints));
    return true;
  } catch (error) {
    console.error('Error saving offline GPS point:', error);
    return false;
  }
};

/**
 * Get all offline GPS points
 */
export const getOfflineGPSPoints = () => {
  try {
    const data = localStorage.getItem(OFFLINE_GPS_POINTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading offline GPS points:', error);
    return [];
  }
};

/**
 * Clear offline GPS points after sync
 */
export const clearOfflineGPSPoints = () => {
  try {
    localStorage.removeItem(OFFLINE_GPS_POINTS_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing offline GPS points:', error);
    return false;
  }
};

/**
 * Save current tracking state to local storage
 * This preserves the user's session if they lose connection mid-delivery
 */
export const saveOfflineTrackingState = (state) => {
  try {
    const offlineData = {
      ...state,
      savedAt: new Date().toISOString(),
      version: 1
    };
    localStorage.setItem(OFFLINE_DATA_KEY, JSON.stringify(offlineData));
    return true;
  } catch (error) {
    console.error('Error saving offline tracking state:', error);
    return false;
  }
};

/**
 * Get saved offline tracking state
 */
export const getOfflineTrackingState = () => {
  try {
    const data = localStorage.getItem(OFFLINE_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading offline tracking state:', error);
    return null;
  }
};

/**
 * Clear offline tracking state
 */
export const clearOfflineTrackingState = () => {
  try {
    localStorage.removeItem(OFFLINE_DATA_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing offline tracking state:', error);
    return false;
  }
};

/**
 * Queue a session for later sync when offline
 */
export const queuePendingSession = (sessionData) => {
  try {
    const pendingSessions = getPendingSessions();
    pendingSessions.push({
      ...sessionData,
      queuedAt: new Date().toISOString(),
      syncAttempts: 0,
      status: 'pending'
    });
    localStorage.setItem(PENDING_SESSIONS_KEY, JSON.stringify(pendingSessions));
    return true;
  } catch (error) {
    console.error('Error queueing pending session:', error);
    return false;
  }
};

/**
 * Get all pending sessions waiting to sync
 */
export const getPendingSessions = () => {
  try {
    const data = localStorage.getItem(PENDING_SESSIONS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading pending sessions:', error);
    return [];
  }
};

/**
 * Update a pending session's status
 */
export const updatePendingSession = (index, updates) => {
  try {
    const pendingSessions = getPendingSessions();
    if (index >= 0 && index < pendingSessions.length) {
      pendingSessions[index] = { ...pendingSessions[index], ...updates };
      localStorage.setItem(PENDING_SESSIONS_KEY, JSON.stringify(pendingSessions));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating pending session:', error);
    return false;
  }
};

/**
 * Remove a pending session after successful sync
 */
export const removePendingSession = (index) => {
  try {
    const pendingSessions = getPendingSessions();
    if (index >= 0 && index < pendingSessions.length) {
      pendingSessions.splice(index, 1);
      localStorage.setItem(PENDING_SESSIONS_KEY, JSON.stringify(pendingSessions));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error removing pending session:', error);
    return false;
  }
};

/**
 * Clear all pending sessions
 */
export const clearPendingSessions = () => {
  try {
    localStorage.removeItem(PENDING_SESSIONS_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing pending sessions:', error);
    return false;
  }
};

/**
 * Get count of pending items for sync indicator
 */
export const getPendingSyncCount = () => {
  const pendingSessions = getPendingSessions();
  const offlinePoints = getOfflineGPSPoints();
  return {
    sessions: pendingSessions.length,
    gpsPoints: offlinePoints.filter(p => !p.synced).length,
    total: pendingSessions.length + offlinePoints.filter(p => !p.synced).length
  };
};

/**
 * Check if there's any data pending sync
 */
export const hasPendingData = () => {
  const counts = getPendingSyncCount();
  return counts.total > 0;
};

export default {
  isOnline,
  saveOfflineGPSPoint,
  getOfflineGPSPoints,
  clearOfflineGPSPoints,
  saveOfflineTrackingState,
  getOfflineTrackingState,
  clearOfflineTrackingState,
  queuePendingSession,
  getPendingSessions,
  updatePendingSession,
  removePendingSession,
  clearPendingSessions,
  getPendingSyncCount,
  hasPendingData
};
